import React, { FC } from 'react';
import { SearchType, TDealerResultListItemProps } from '../../../../types/dealerLocatorLanding.types';
import Icon from '../../../../../@shared/icon';
import { iconSize } from '../../../../../../constants/icon.constants';
import dealerPinSvg from '../../../../../../assets/dealer-pin.svg';
import dealerPinActiveSvg from '../../../../../../assets/dealer-pin-active.svg';
import DictionaryTerm from '../../../../../@shared/dictionaryTerm';

import '../../styles.scss';
import RoutableNotEditableLink from '../../../../../@shared/routableNotEditableLink';
import { appendToUrlPath } from '../../../../../../utils/link.utils';
import { useLocation } from 'react-router-dom';
import { googleClickDealerLocatorGetDetails, googleDealerLocatorContactClick } from '../../../../../../utils/analytics';
import {
  dealerLocatorContactClick,
  dealerLocatorDetailView,
  dealerLocatorGetDirections,
} from '../../../../../../constants/analytics.constants';
import useSearchText from '../../../../hooks/useSearchText.effect';
import { BreakpointsInPx } from '../../../../../../constants/breakpoints.constants';
import useMediaQuery from '../../../../../../hooks/useMediaQuery';

enum arrowIcons {
  up = 'arrow-chevron-up',
  down = 'arrow-chevron-down',
}

const GOOGLE_DIRECTIONS_URL = 'https://www.google.com/maps/search/?api=1&query=';

const DealerResultsListItem: FC<TDealerResultListItemProps> = ({
  model,
  dealer,
  markerNumber,
  clickedDealerNumber,
  dealerIdForPinWithHover,
  setClickedDealerNumber,
  setDealerIdForPinWithHover,
  setDealerIdPinHovered,
  country,
  region,
}) => {
  const dealership = dealer.dealership;
  const expanded = clickedDealerNumber === dealership.dealerNumber;
  const arrowIcon = expanded ? arrowIcons.up : arrowIcons.down;
  const hasHoverActive = dealerIdForPinWithHover === dealership.dealerNumber;
  const pinIcon = hasHoverActive ? dealerPinActiveSvg : dealerPinSvg;
  const distance = Number.parseFloat(dealer?.distance ?? '');
  const distanceText = !Number.isNaN(distance) ? `${(Math.round(distance * 100) / 100).toFixed(2)} ${model.distanceUnits}` : '';
  const isCertified = !!dealership?.dealershipAttributes?.awardDesignations?.length;
  const location = useLocation();
  const dealerNameForUrl =
    (dealership?.dealerName ?? '')
      .split(/[^a-zA-Z0-9]+/) // Split by non-alphanumeric characters
      .filter(Boolean) // Remove empty segments
      .join('-')
      .toLocaleLowerCase() ?? '';

  const dealerDetailPagePath = appendToUrlPath(
    model.currentPagePath,
    `${encodeURIComponent(dealerNameForUrl || '-')}/${encodeURIComponent(dealership.dealerNumber)}${location?.search ?? ''}`
  );

  const getDirectionsUrl = (): string => {
    return `${GOOGLE_DIRECTIONS_URL}${dealership?.latitude},${dealership?.longitude}`;
  };

  // GTM Event Handling Logic
  const countryIsoCodeWithFallbackForGtm =
    model.searchType === SearchType?.SearchByLocationOrDealer || SearchType?.NoSearch
      ? model.defaultCountry
      : country?.IsoCode || model.defaultCountry;

  const getCountryName = (isoCode, countries) => countries?.find((country) => country?.IsoCode === isoCode)?.Country;

  const determineSelectedCountry = (model, isoCode) =>
    model?.searchType === SearchType?.SearchByLocationOrDealer ? getCountryName(isoCode, model?.countries) || isoCode : country?.Country;
  const selectedCountry = determineSelectedCountry(model, countryIsoCodeWithFallbackForGtm);

  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx['tbl-l']})`);

  const [searchText] = useSearchText(''); // eslint-disable-line

  const handleGetDetailsClick = (dealerClickEvent) => {
    return googleClickDealerLocatorGetDetails(
      dealerClickEvent,
      country?.Country ?? selectedCountry,
      region?.name ?? searchText,
      dealership?.dealerName,
      isCertified ? 'Yes' : 'No',
    );
  };
  const handleContactClick = (contactMethod) => {
    return googleDealerLocatorContactClick(dealerLocatorContactClick, contactMethod, dealership?.dealerName);
  };

  return (
    <li
      className={`dealer-locator-landing-dealer-results__item ${
        hasHoverActive ? 'dealer-locator-landing-dealer-results__item--active' : ''
      }`}
      onMouseEnter={() =>{
        if(isWide) {
          setDealerIdForPinWithHover(dealership.dealerNumber);
          setDealerIdPinHovered(true);
        }
      }}
      onMouseLeave={() => {
        if(isWide) {
          setDealerIdForPinWithHover(null);
          setDealerIdPinHovered(null);
        }
      }}
    >
      <div className="dealer-locator-landing-dealer-results__item-header">
        <div>
          {isCertified && (
            <p className="dealer-locator-landing-dealer-results__item-certified">
              <DictionaryTerm keyword="cnhi-dealerLocatorCertifiedDealer" />
            </p>
          )}
          <div className="dealer-locator-landing-dealer-results__item-title">{dealership.dealerName}</div>
          <div className="dealer-locator-landing-dealer-results__item-address">
          {dealership.shippingAddress1}, {dealership.shippingCity}, {dealership.shippingStateProv || ''} {dealership.shippingZip}
          </div>
        </div>
        <div className="dealer-locator-landing-dealer-results__item-marker-wrapper">
          <span className="dealer-locator-landing-dealer-results__item-marker">
            <span className="dealer-locator-landing-dealer-results__item-label">{markerNumber}</span>
            <img src={pinIcon} alt="" />
          </span>
          <p className="dealer-locator-landing-dealer-results__item-dealer-distance">{distanceText}</p>
        </div>
      </div>
      <div>
        <RoutableNotEditableLink
          className="dealer-locator-landing-dealer-results__item-get-directions-cta"
          target="_blank"
          href={getDirectionsUrl()}
          onClick={() => handleGetDetailsClick(dealerLocatorGetDirections)}
        >
          <DictionaryTerm keyword="cnhi-dealerLocatorGetDirections" />
          <Icon
            color="primary"
            iconName="geolocation-distance-full"
            extraClassName="dealer-locator-landing-dealer-results__item-get-directions-icon"
            size={iconSize['dsk']}
          />
        </RoutableNotEditableLink>
      </div>

      <div
        className={`dealer-locator-landing-dealer-results__item-show-more-wrapper ${
          expanded && 'dealer-locator-landing-dealer-results__item-show-more-wrapper--visible'
        }`}
      >
        <div className="dealer-locator-landing-dealer-results__item-contact-details">
          {dealership.shippingPhone && (
            <p className="dealer-locator-landing-dealer-results__contact-row">
              <Icon
                color="primary"
                iconName="phone-messages-phone"
                extraClassName="dealer-locator-landing-dealer-results__contact-icon"
                size={iconSize['dsk']}
              />
              <RoutableNotEditableLink
                className="dealer-locator-landing-dealer-results__contact-link"
                href={`tel:${dealership.shippingPhone}`}
                onClick={() => handleContactClick('Phone')}
              >
                {dealership.shippingPhone}
              </RoutableNotEditableLink>
            </p>
          )}
          {dealership.dealerEmail && (
            <p className="dealer-locator-landing-dealer-results__contact-row">
              <Icon
                color="primary"
                iconName="social-sharing-email"
                extraClassName="dealer-locator-landing-dealer-results__contact-icon"
                size={iconSize['dsk']}
              />
              <RoutableNotEditableLink
                className="dealer-locator-landing-dealer-results__contact-link"
                href={`mailto:${dealership.dealerEmail}`}
                onClick={() => handleContactClick('Email')}
              >
                {dealership.dealerEmail}
              </RoutableNotEditableLink>
            </p>
          )}
          {dealership.dealershipAttributes?.website && (
            <p className="dealer-locator-landing-dealer-results__contact-row">
              <Icon
                color="primary"
                iconName="social-sharing-globe"
                extraClassName="dealer-locator-landing-dealer-results__contact-icon"
                size={iconSize['dsk']}
              />
              <RoutableNotEditableLink
                className="dealer-locator-landing-dealer-results__contact-link"
                target="_blank"
                href={dealership.dealershipAttributes?.website}
                onClick={() => handleContactClick('Website')}
              >
                {dealership.dealershipAttributes?.website}
              </RoutableNotEditableLink>
            </p>
          )}
        </div>
        <div className="dealer-locator-landing-dealer-results__item-contact-awards">
          {dealership?.dealershipAttributes?.awardDesignations?.map((award) => (
            <img
              key={award?.designationName}
              className="dealer-locator-landing-dealer-results__item-contact-awards-img"
              src={award?.imageUrl}
              alt={award?.designationName}
            />
          ))}
        </div>
      </div>

      {dealership?.hasInternalDetails === 'true' && (
        <RoutableNotEditableLink className="dealer-locator-landing-dealer-results__item-see-details-cta" href={dealerDetailPagePath} onClick={() => handleGetDetailsClick(dealerLocatorDetailView)}>
          <DictionaryTerm keyword="cnhi-DealerLocatorSeeDetails" />
        </RoutableNotEditableLink>
      )}
      <div
        className="dealer-locator-landing-dealer-results__show-more-icon"
        onClick={() => {
          setClickedDealerNumber(expanded ? null : dealership.dealerNumber);
          // Highlighting marker of expanded card for mobile
          if (!isWide) {
            setDealerIdForPinWithHover(expanded ? null : dealer.dealership.dealerNumber);
            setDealerIdPinHovered(expanded ? null : true);
          }
        }}
      >
        <Icon iconName={arrowIcon} size={iconSize['dsk']} color="primary" />
      </div>
    </li>
  );
};

export default DealerResultsListItem;
